import { FileOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { XIcon } from '@heroicons/react/outline';
import { Affix, Button, Modal, Tooltip } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import HeadphoneIcon from 'assets/Icons/headphoneIcon';
import Star from 'assets/Icons/Star';
import ShareIcon from 'assets/ShareIcons/ShareIcon';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import ShareButton from '@/components/PdfReader/ShareButton';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { notify } from '@/components/shared/notify';

import { authModalControl } from '@/atoms/authModalControl';
import { shareModalVisibleState } from '@/atoms/modalAtom';
import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { mobileToolboxVisibleAtom } from '@/atoms/pdfStates/mobileToolboxVisible';
import { User } from '@/utils/interfaces/User';

interface Props {
  isFullScreen: boolean;
  toggleFullScreen: () => void;
  showDrawer: () => void;
  visible: boolean;
  isIphone: () => boolean;
  fullScreenIcon: React.ReactNode;
  playAudio?: () => void;
  showAudio?: boolean;
  hideAudioBook?: boolean;
  token?: string;
  userData: User;
  onOpenDrawer: () => void;
  book_id?: string;
  isIframe?: boolean;
}

const ToolBoxSidebar: React.FC<Props> = ({
  isFullScreen,
  toggleFullScreen,
  showDrawer,
  visible,
  isIphone,
  fullScreenIcon,
  playAudio,
  showAudio,
  hideAudioBook = false,
  token,
  userData,
  onOpenDrawer,
  isIframe = false,
  book_id,
}) => {
  const [, setVisible] = useRecoilState(authModalControl);
  const [, setIsModalVisibleShare] = useRecoilState(shareModalVisibleState);
  const [mobileToolboxVisible, setMobileToolboxVisible] = useRecoilState(
    mobileToolboxVisibleAtom,
  );

  const [, setDrawerState] = useRecoilState(drawerPdfAtom);
  const router = useRouter();
  const screens = useBreakpoint();
  const [isScrolledTop, setIsScrolledTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolledTop(
        (window.scrollY || document.documentElement.scrollTop) <= 10,
      );
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const askAiEventLog = () => {
    const eventParams: EventDetails = {
      event_name: 'screen_view',
      source: 'ask_ai',
      source_type: 'book',
      source_id: book_id || '',
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: { click_source: 'books_reading_screen' },
    };

    logEvent({
      eventDetails: eventParams,
      userData,
      pathname: router.pathname,
    });
  };

  const handleShareClick = () => {
    if (!token) {
      setVisible((prev) => ({ ...prev, login: true }));
      notify('Please login to share', 'warning');
      setIsModalVisibleShare(false);
    } else {
      setIsModalVisibleShare((prev) => !prev);
    }
  };

  const renderButton = (
    icon: React.ReactNode,
    label: string,
    onClick?: () => void,
    bgColor = 'bg-grayZl-850',
    textColor = 'text-white',
    key: number = 0,
  ) => (
    <div
      key={key}
      className={clsx(
        !screens.lg &&
          'bg-cartBgColor rounded-xl md:w-[160px] md:h-[160px] p-4 flex justify-center items-center',
      )}
      onClick={() => {
        if (!screens.lg) setMobileToolboxVisible(false);
        onClick?.();
      }}
    >
      <div className='flex flex-col items-center space-y-1'>
        <button
          className={clsx(
            'w-12 h-12 rounded-full flex items-center justify-center transition hover:opacity-80',
            bgColor,
            textColor,
          )}
        >
          {icon}
        </button>
        <span className='text-xs text-white font-light pt-2 text-center'>
          {label}
        </span>
      </div>
    </div>
  );

  const buttonConfigs = [
    !isIphone() && {
      icon: screens.lg ? (
        <Tooltip title='Full screen'>{fullScreenIcon}</Tooltip>
      ) : (
        fullScreenIcon
      ),
      label: 'Full Screen',
      onClick: toggleFullScreen,
    },
    !isIframe && {
      icon: (
        <UnorderedListOutlined
          className={clsx(visible ? 'text-primary' : 'text-white')}
          style={{ fontSize: 20 }}
        />
      ),
      label: 'Index',
      onClick: showDrawer,
    },
    !isIframe && {
      icon: <FileOutlined className='rotate-180 scale-x-[-1]' />,
      label: 'My Notes',
      onClick: () =>
        setDrawerState((prev) => ({
          ...prev,
          notesDrawer: !prev.notesDrawer,
        })),
    },
    hideAudioBook && {
      icon: showAudio ? (
        <XIcon className='w-5 h-5 text-black' />
      ) : (
        <HeadphoneIcon width='20' color='black' />
      ),
      label: 'Audio Book',
      onClick: playAudio,
      bgColor: 'bg-white',
      textColor: 'text-black',
    },
    !isIframe && {
      icon: <Star width='16' />,
      label: 'Ask AI',
      onClick: () => {
        onOpenDrawer();
        askAiEventLog();
      },
      bgColor: 'bg-gradient-to-r from-purple-600 via-indigo-500 to-blue-400',
    },
    {
      icon: <ShareIcon width='18' />,
      label: 'Share',
      onClick: handleShareClick,
    },
  ].filter(
    (config) => config !== false && config !== null && config !== undefined,
  );

  const buttons = buttonConfigs.map((cfg, idx) =>
    renderButton(
      cfg.icon,
      cfg.label,
      cfg.onClick,
      cfg.bgColor,
      cfg.textColor,
      idx,
    ),
  );
  const lastIsOdd = buttons.length % 2 !== 0;
  const gridButtons = lastIsOdd ? buttons.slice(0, -1) : buttons;
  const lastCentered = lastIsOdd ? buttons.at(-1) : null;

  if (!screens.lg) {
    return (
      <Modal
        open={mobileToolboxVisible}
        onCancel={() => setMobileToolboxVisible(false)}
        footer={null}
        closable={false}
        centered
        width='100vw'
        classNames={{
          body: 'h-screen bg-grayZl-950 flex flex-col p-0',
          mask: 'bg-grayZl-950',
          header: 'hidden',
          footer: 'hidden',
          content: 'bg-grayZl-950 rounded-none shadow-none',
        }}
        {...(isFullScreen && { getContainer: false })}
        className='!m-0'
      >
        <div className='flex items-center max-h-[60%] justify-between text-white text-lg md:px-6 md:pt-6'>
          <span className='font-semibold text-center'>Menu</span>
          <Button
            onClick={() => setMobileToolboxVisible(false)}
            className='border-none'
          >
            <XIcon className='w-4 text-white' />
          </Button>
        </div>

        <div className='flex h-full items-center justify-center'>
          <div className='flex flex-col items-center w-full gap-6'>
            <div className='grid grid-cols-2 gap-4 w-full md:w-fit'>
              {gridButtons.map((btn, idx) => (
                <div key={idx}>{btn}</div>
              ))}
            </div>
            {lastCentered && (
              <div className='min-w-[130px] xxxs:min-w-[150px]'>
                {lastCentered}
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Affix className='h-full' offsetTop={0}>
      <div className='right-0 top-0 min-h-[100vh] w-[94px] bg-grayZl-950 flex flex-col justify-between py-3 relative z-30'>
        <div className='flex flex-col items-center space-y-6'>
          {buttons.slice(0, -1)}
        </div>
        <ShouldRender check={!isIframe}>
          <div
            className={clsx(
              isScrolledTop && !isFullScreen ? 'pb-16' : 'pb-0',
              'flex flex-col items-center space-y-1 mt-auto transition-[padding] duration-300 ease-in-out',
            )}
          >
            {isFullScreen ? (
              buttons.at(-1)
            ) : (
              <ShareButton
                type='only-icon-title'
                isLoggedIN={!!token}
                isPdfReader={true}
                productId={book_id}
              />
            )}
          </div>
        </ShouldRender>
      </div>
    </Affix>
  );
};

export default ToolBoxSidebar;
